// src/components/features/brands/SocialAccountsManager.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  InputAdornment,
  Link,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Language as LanguageIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  BarChart as AnalyticsIcon,
} from '@mui/icons-material';

// Import custom components
import { Card } from '../../design-system';
import { EmptyState } from '../../design-system/components/core';

// Social platform options
const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: FacebookIcon },
  { value: 'instagram', label: 'Instagram', icon: InstagramIcon },
  { value: 'twitter', label: 'Twitter', icon: TwitterIcon },
  { value: 'linkedin', label: 'LinkedIn', icon: LinkedInIcon },
  { value: 'youtube', label: 'YouTube', icon: YouTubeIcon },
  { value: 'tiktok', label: 'TikTok', icon: LanguageIcon },
  { value: 'snapchat', label: 'Snapchat', icon: LanguageIcon },
  { value: 'pinterest', label: 'Pinterest', icon: LanguageIcon },
  { value: 'other', label: 'Other', icon: LanguageIcon },
];

// Platform icon component
const PlatformIcon = ({ platform, ...props }) => {
  const platformInfo = SOCIAL_PLATFORMS.find(p => p.value === platform) || SOCIAL_PLATFORMS.find(p => p.value === 'other');
  const Icon = platformInfo.icon;
  return <Icon {...props} />;
};

// Format URL with protocol if missing
const formatUrl = (url) => {
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

const SocialAccountsManager = ({
  accounts = [],
  onUpdate,
  onAdd,
  onDelete,
  canEdit = true,
  canViewCredentials = false,
}) => {
  const theme = useTheme();
  
  // Local state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAccount, setCurrentAccount] = useState({
    platform: 'facebook',
    handle: '',
    url: '',
    followers: 0,
    isActive: true,
    credentials: {
      isStored: false,
      reference: ''
    }
  });
  
  // Handle dialog open for new account
  const handleAddAccount = () => {
    setCurrentAccount({
      platform: 'facebook',
      handle: '',
      url: '',
      followers: 0,
      isActive: true,
      credentials: {
        isStored: false,
        reference: ''
      }
    });
    setEditMode(false);
    setDialogOpen(true);
  };
  
  // Handle dialog open for editing
  const handleEditAccount = (account) => {
    setCurrentAccount({ ...account });
    setEditMode(true);
    setDialogOpen(true);
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentAccount(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCurrentAccount(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  // Handle credentials checkbox
  const handleCredentialsChange = (e) => {
    const { checked } = e.target;
    setCurrentAccount(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        isStored: checked
      }
    }));
  };
  
  // Handle form submission
  const handleSubmit = () => {
    // Validate form
    if (!currentAccount.handle.trim()) {
      alert('Handle is required');
      return;
    }
    
    // Format URL if provided
    if (currentAccount.url) {
      currentAccount.url = formatUrl(currentAccount.url);
    }
    
    if (editMode) {
      onUpdate(currentAccount);
    } else {
      onAdd(currentAccount);
    }
    
    setDialogOpen(false);
  };
  
  // Handle account deletion
  const handleDeleteAccount = (accountId) => {
    if (window.confirm('Are you sure you want to delete this social media account?')) {
      onDelete(accountId);
    }
  };
  
  return (
    <Box>
      {/* Header with add button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Social Media Accounts
        </Typography>
        
        {canEdit && (
          <Button
            variant="contained"
            
            startIcon={<AddIcon />}
            onClick={handleAddAccount}
          >
            Add Account
          </Button>
        )}
      </Box>
      
      {/* Account List */}
      {accounts.length === 0 ? (
        <EmptyState
          title="No Social Media Accounts"
          description="Add social media accounts that you manage for this brand"
          icon={<LanguageIcon sx={{ fontSize: 64 }} />}
          action={
            canEdit ? (
              <Button
                variant="contained"
                
                startIcon={<AddIcon />}
                onClick={handleAddAccount}
              >
                Add Account
              </Button>
            ) : null
          }
        />
      ) : (
        <Grid container spacing={3}>
          {accounts.map((account) => (
            <Grid item xs={12} sm={6} md={4} key={account._id || account.platform + account.handle}>
              <Card magical={false}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 2,
                      }}
                    >
                      <PlatformIcon platform={account.platform} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">
                        {SOCIAL_PLATFORMS.find(p => p.value === account.platform)?.label || 'Other'}
                        {!account.isActive && (
                          <Chip
                            label="Inactive"
                            size="small"
                            color="default"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        @{account.handle}
                      </Typography>
                    </Box>
                    {account.credentials?.isStored && (
                      <Tooltip title={canViewCredentials && account.credentials?.reference ? `Reference: ${account.credentials.reference}` : "Credentials Stored"}>
                        <LockIcon 
                          color="success"
                          fontSize="small"
                          sx={{ mr: 1 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  
                  {account.url && (
                    <Box sx={{ mb: 1 }}>
                      <Link 
                        href={formatUrl(account.url)} 
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: '0.875rem',
                          color: theme.palette.text.secondary,
                          wordBreak: 'break-all'
                        }}
                      >
                        <LanguageIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {account.url}
                      </Link>
                    </Box>
                  )}
                  
                  {account.followers > 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <AnalyticsIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {account.followers.toLocaleString()} followers
                      </Box>
                    </Typography>
                  )}
                </CardContent>
                
                {canEdit && (
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditAccount(account)}
                      title="Edit account"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteAccount(account._id)}
                      title="Delete account"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Social Media Account' : 'Add Social Media Account'}
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="platform-label">Platform</InputLabel>
                <Select
                  labelId="platform-label"
                  id="platform"
                  name="platform"
                  value={currentAccount.platform}
                  onChange={handleInputChange}
                  label="Platform"
                  disabled={editMode} // Can't change platform when editing
                >
                  {SOCIAL_PLATFORMS.map((platform) => (
                    <MenuItem key={platform.value} value={platform.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <platform.icon sx={{ mr: 1 }} />
                        {platform.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Handle or Username"
                name="handle"
                value={currentAccount.handle}
                onChange={handleInputChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">@</InputAdornment>,
                }}
                placeholder="username"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Profile URL"
                name="url"
                value={currentAccount.url}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://platform.com/username"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LanguageIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Followers"
                name="followers"
                type="number"
                value={currentAccount.followers}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentAccount.isActive}
                    onChange={handleCheckboxChange}
                    name="isActive"
                  />
                }
                label="Active Account"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Account Credentials
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={currentAccount.credentials?.isStored || false}
                    onChange={handleCredentialsChange}
                    name="credentialsStored"
                  />
                }
                label="Store credentials for this account"
              />
              
              {currentAccount.credentials?.isStored && (
                <>
                  {canViewCredentials ? (
                    <TextField
                      label="Credentials Reference"
                      name="credentialsReference"
                      value={currentAccount.credentials?.reference || ''}
                      onChange={(e) => {
                        setCurrentAccount(prev => ({
                          ...prev,
                          credentials: {
                            ...prev.credentials,
                            reference: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                      placeholder="Reference to secure storage (e.g. LastPass entry ID)"
                      sx={{ mt: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <LockIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />
                      Credential reference is stored but you don't have permission to view or edit it.
                    </Typography>
                  )}
                </>
              )}
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Note: Actual passwords are not stored in this system. {canViewCredentials && 'Use credentials reference to indicate where the secure credentials are stored.'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
          >
            {editMode ? 'Save Changes' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialAccountsManager;